/**
 * Program Structure 1042 — BDU Programme Syllabus
 *
 * Split section: a tinted left column with the heading, semester tabs, the subject
 * card and the brochure CTA; the right half is the same background art, uncovered.
 *
 * One flat metadata row = one subject, the same shape Program Structure 4 stores:
 *  - a row with `semester_name` joins that semester tab and shows its `credits`;
 *  - a row with `elective_name` joins that elective group and shows its own
 *    `subject_semester`;
 *  - `semester_is_elective_based` marks the whole semester "as per elective",
 *    hiding its subject list and showing the elective picker instead.
 *
 * The brochure comes from the page's bound course (as it does on BDU), so there is
 * no brochure-URL field here.
 */

export const PROGRAM_STRUCTURE_1042_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Subjects",
  },

  DEFAULT_ROW: {
    semester_name: "SEMESTER 1",
    semester_is_elective_based: false,
    subject_name: "",
    credits: "",
    elective_name: "",
    subject_semester: "",
    priority: 0,
    status: true,
  },

  PROGRAM_STRUCTURE_1042_FIELDS_CONFIG: [
    {
      fieldName: "title",
      label: "Heading",
      value: "Programme Syllabus",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Programme Syllabus",
    },
    {
      fieldName: "title_text_color",
      label: "Heading + tab text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "section_background_image",
      label: "Background image (fills both halves)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload full-bleed background",
      defaultValue: "",
      filterCategory: "images",
    },
    {
      fieldName: "section_background_image_alt",
      label: "Background image alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      fieldName: "container_background_color",
      label: "Left column fill (768px and up)",
      value: "#394E63",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#394E63",
    },
    {
      fieldName: "header_section_color",
      label: "Left column scrim below 768px (rgba)",
      value: "rgba(4, 12, 20, 0.63)",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "rgba(4, 12, 20, 0.63)",
      defaultValue: "rgba(4, 12, 20, 0.63)",
    },
    {
      fieldName: "subject_container_color",
      label: "Subject / elective card surface",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "module_active_text_color",
      label: "Selected semester tab colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "module_inactive_text_color",
      label: "Semester tab colour (inactive)",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "electives_tab_label",
      label: "Elective card heading",
      value: "Elective courses",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Elective courses",
    },
    {
      fieldName: "electives_semester_label",
      label: "Elective-based semester pill label",
      value: "as per elective",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "as per elective",
    },
    {
      fieldName: "subjects_column_label",
      label: "Elective table — left column header",
      value: "Subjects",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Subjects",
    },
    {
      fieldName: "credits_column_label",
      label: "Elective table — right column header",
      value: "Semester",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Semester",
    },
    {
      fieldName: "electives_empty_message",
      label: "Elective table — empty state message",
      value: "There are no subjects in this elective",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "There are no subjects in this elective",
    },
    {
      fieldName: "gradient_color_1",
      label: "Heading rule below 768px — start (blue)",
      value: "#1F3D56",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#1F3D56",
    },
    {
      fieldName: "gradient_color_2",
      label: "Heading rule below 768px — end (blue)",
      value: "#7CB5E6",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#7CB5E6",
    },
    {
      fieldName: "gradient_color_3",
      label: "Accent gradient — start (brochure CTA + elective pill)",
      value: "#FF0013",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FF0013",
    },
    {
      fieldName: "gradient_color_4",
      label: "Accent gradient — end (brochure CTA + elective pill)",
      value: "#990013",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#990013",
    },
    {
      fieldName: "card_background_color",
      label: "Elective pill background",
      value: "#FEF0F3",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FEF0F3",
    },
    {
      fieldName: "content_panel_background_color",
      label: "Elective card inner surfaces",
      value: "#F0F0F0",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#F0F0F0",
    },
    {
      fieldName: "brochure_cta_text",
      label: "Brochure CTA label (empty = hide)",
      value: "Download Brochure",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Download Brochure",
    },
    {
      fieldName: "brochure_cta_text_color",
      label: "Brochure CTA text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "brochure_cta_is_cta",
      label: "Brochure — is CTA (lead popup, then download)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder:
        "On = lead capture popup then download; off = open the bound course's brochure directly",
      defaultValue: true,
    },
    {
      fieldName: "status",
      label: "Section status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Semester tab",
      fieldName: "semester_name",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. SEMESTER 1 — leave empty on elective rows",
      defaultValue: "",
    },
    {
      header: "Semester is elective-based",
      fieldName: "semester_is_elective_based",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder:
        "Hides this semester's subject list and shows the elective picker instead",
      defaultValue: false,
    },
    {
      header: "Subject",
      fieldName: "subject_name",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Managerial Economics",
      defaultValue: "",
    },
    {
      header: "Credits (blank or 0 hides the value)",
      fieldName: "credits",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. 4",
      defaultValue: "",
    },
    {
      header: "Elective group (moves this row under the elective picker)",
      fieldName: "elective_name",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Finance",
      defaultValue: "",
    },
    {
      header: "Subject's semester (electives only)",
      fieldName: "subject_semester",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. 3 sem",
      defaultValue: "",
    },
    {
      header: "Sort order",
      fieldName: "priority",
      value: 0,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 0,
    },
    {
      header: "Active",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],
};
